import { ThirdPartyConfigsSync } from '@atlassian/rovo-connectors-config/configs-sync';
import {
	type ConnectorSource,
	FEDERATED_CONNECTOR_SOURCE,
	FULL_CONNECTOR_SOURCE,
	LITE_CONNECTOR_SOURCE,
	LITE_CONNECTOR_SOURCE_PREFIX,
	SMARTLINKS_CONNECTOR_SOURCE,
	SMARTLINKS_RESOURCE_TYPE_PREFIX,
} from '@atlassian/rovo-connectors-config/schemas/types';
import { type ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import { type ConnectorRow } from '../thirdPartyPromptTypes.ts';

import { resolveAppIdForConnectorName } from './connectorTypeNames.ts';

// A resource type is the full-connector name, e.g. `google-drive-connector`.
const productKeyByResourceType = new Map<string, ProductKeys3P>();

ThirdPartyConfigsSync.forEach((config, productKey) => {
	if (config.resourceType) {
		productKeyByResourceType.set(config.resourceType, productKey);
	}
});

const isProductKey = (value: string): value is ProductKeys3P =>
	ThirdPartyConfigsSync.has(value as ProductKeys3P);

/** Static config for a connector name. Standard names hit the resource-type map; aliases fall back to app id. */
const staticConfigForName = (connectorName: string) => {
	let productKey = productKeyByResourceType.get(connectorName);
	if (!productKey) {
		const appId = resolveAppIdForConnectorName(connectorName);
		productKey = appId && isProductKey(appId) ? appId : undefined;
	}

	return productKey ? ThirdPartyConfigsSync.get(productKey) : undefined;
};

/** `undefined` means the name maps to no known product, so it fails closed. */
export const classifyConnectorRow = (row: ConnectorRow): ConnectorSource | undefined => {
	if (row.name.startsWith(SMARTLINKS_RESOURCE_TYPE_PREFIX)) {
		return SMARTLINKS_CONNECTOR_SOURCE;
	}

	if (row.name.startsWith(LITE_CONNECTOR_SOURCE_PREFIX)) {
		return LITE_CONNECTOR_SOURCE;
	}

	const config = staticConfigForName(row.name);
	if (!config) {
		return undefined;
	}

	if (row.connectorSyncType === FEDERATED_CONNECTOR_SOURCE || config.isFederated) {
		return FEDERATED_CONNECTOR_SOURCE;
	}

	return FULL_CONNECTOR_SOURCE;
};
